import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useLayout } from "@/contexts/LayoutContext";
import { useIsSystemAdmin } from "@/hooks/useIsSystemAdmin";
import { useIsTenantAdmin } from "@/hooks/useIsTenantAdmin";
import { useTenants } from "@/hooks/useTenants";
import { LoadingSpinner } from "@/components/loading/LoadingSpinner";
import { TenantCard } from "@/components/admin/TenantCard";
import { CreateTenantModal } from "@/components/admin/CreateTenantModal";
import { Button } from "@/components/button";
import { Building2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

export function TenantListPage() {
  const { t } = useTranslation(["admin"]);
  const { navbarHeightRem } = useLayout();
  const navigate = useNavigate();
  const { isSystemAdmin, isLoading: isAdminLoading } = useIsSystemAdmin();
  const { isTenantAdmin, isLoading: isTenantAdminLoading } = useIsTenantAdmin();
  const { tenants, isLoading: isTenantsLoading } = useTenants();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const isAuthLoading = isAdminLoading || isTenantAdminLoading;

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isSystemAdmin && !isTenantAdmin) {
      navigate({
        to: "/home",
        search: { redirect: undefined, downloadJobId: undefined },
      });
    }
  }, [isAuthLoading, isSystemAdmin, isTenantAdmin, navigate]);

  if (isAuthLoading || isTenantsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="64px" />
      </div>
    );
  }

  return (
    <div
      className="flex w-full flex-col gap-6"
      style={{ paddingTop: `${navbarHeightRem + 1}rem` }}
    >
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">{t("admin:tenants.title")}</h1>
        {isSystemAdmin && (
          <Button
            testId="create-tenant-button"
            onClick={() => setIsCreateOpen(true)}
            icon={<Plus className="h-4 w-4" />}
            label={t("admin:tenants.createButton")}
          />
        )}
      </div>

      {tenants.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
          <Building2 className="h-12 w-12" aria-hidden="true" />
          <p className="text-lg">{t("admin:tenants.emptyStateTitle")}</p>
          <p className="text-sm">{t("admin:tenants.emptyStateDescription")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tenants.map((tenant) => (
            <TenantCard key={tenant.tenantId} tenant={tenant} />
          ))}
        </div>
      )}

      {isSystemAdmin && (
        <CreateTenantModal open={isCreateOpen} onOpenChange={setIsCreateOpen} />
      )}
    </div>
  );
}
